import {
  Button,
  SkeletonCheckbox,
  SkeletonTable,
  SkeletonText,
  Table,
  type TableColumn,
} from '@dev-spendesk/grapes';
import React, { useMemo } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { type InfiniteQueryState } from 'src/core/api/queryState';
import { useAccountingSoftwareName } from 'src/core/modules/accounting-integration/apis';
import { type SyncOperation } from 'src/core/modules/accounting-integration/apis/useGetSyncOperation';
import { type SyncMode } from 'src/core/modules/accounting-integration/apis/useSetSyncSettings';
import { useHasMapSync } from 'src/core/modules/accounting-integration/hooks/useHasMapSync';
import { type AnalyticalFieldMappingV2 } from 'src/core/modules/bookkeep';
import { MappedValue } from 'src/core/modules/company/cost-centers/components/CostCentersPage/MappedValue';

import { CustomFieldRowActions } from '../../components/CustomFieldValuesList/CustomFieldRowActions';
import { type CustomFieldValue } from '../CustomFieldValuesListContainer/hooks';

type Props = {
  customFieldValuesQueryState: InfiniteQueryState<CustomFieldValue[]>;
  fieldMapping?: AnalyticalFieldMappingV2 | undefined;
  showUpdateCustomFieldValueModal: () => void;
  showDeleteCustomFieldValueModal: () => void;
  onSelectedValuesChange: (selectedValueIds: string[]) => void;
  selectedValueIds: string[];
  isMapSyncEnabled: boolean;
  syncOperation: SyncOperation;
  isSearching?: boolean;
  searchTerm?: string;
  syncMode?: SyncMode;
};

export const CustomFieldLimitlessListContainer = ({
  customFieldValuesQueryState,
  fieldMapping,
  showUpdateCustomFieldValueModal,
  showDeleteCustomFieldValueModal,
  onSelectedValuesChange,
  selectedValueIds,
  isSearching = false,
  searchTerm,
  syncMode,
  isMapSyncEnabled,
  syncOperation,
}: Props) => {
  const { t } = useTranslation('global');
  const hasMapSyncFeature = useHasMapSync();
  const { accountingSoftwareName } = useAccountingSoftwareName();

  const mappedValuesByInternalId = useMemo(() => {
    if (!fieldMapping?.values) {
      return {};
    }

    return fieldMapping.values.reduce<Record<string, string | undefined>>(
      (accumulator, mappingValue) => {
        if (mappingValue.internalId) {
          accumulator[mappingValue.internalId] = mappingValue.externalName;
        }
        return accumulator;
      },
      {},
    );
  }, [fieldMapping]);

  const tableColumns: TableColumn<CustomFieldValue>[] = useMemo(() => {
    const showMappedValueColumn =
      hasMapSyncFeature && fieldMapping && syncMode !== 'none';

    const nameColumn: TableColumn<CustomFieldValue> = {
      id: 'name',
      header: t('misc.name'),
      renderCell: (row) => (
        <div className="group flex w-full items-center justify-between gap-16">
          <span className="flex-1">{row.name}</span>
          {!showMappedValueColumn && (
            <div className="flex shrink-0 gap-8 opacity-0 group-hover:opacity-100">
              <CustomFieldRowActions
                customFieldValueId={row.id}
                showUpdateCustomFieldValueModal={
                  showUpdateCustomFieldValueModal
                }
                showDeleteCustomFieldValueModal={
                  showDeleteCustomFieldValueModal
                }
                customFieldValueName={row.name}
              />
            </div>
          )}
        </div>
      ),
    };

    const mappedValueColumn: TableColumn<CustomFieldValue>[] =
      showMappedValueColumn
        ? [
            {
              id: 'mappedValue',
              header: t('customFields.settings.mappedValueColumn'),
              renderCell: (row) => (
                <div className="group flex w-full items-center justify-between gap-16">
                  <MappedValue value={mappedValuesByInternalId[row.id]} />
                  <div className="flex shrink-0 gap-8 opacity-0 group-hover:opacity-100">
                    <CustomFieldRowActions
                      customFieldValueId={row.id}
                      showUpdateCustomFieldValueModal={
                        showUpdateCustomFieldValueModal
                      }
                      showDeleteCustomFieldValueModal={
                        showDeleteCustomFieldValueModal
                      }
                      customFieldValueName={row.name}
                      syncMode={syncMode}
                    />
                  </div>
                </div>
              ),
            },
          ]
        : [];

    return [nameColumn, ...mappedValueColumn];
  }, [
    fieldMapping,
    hasMapSyncFeature,
    mappedValuesByInternalId,
    showDeleteCustomFieldValueModal,
    showUpdateCustomFieldValueModal,
    t,
  ]);

  if (isSearching && customFieldValuesQueryState.status === 'loading') {
    return (
      <SkeletonTable
        columns={[
          {
            cell: (
              <>
                <SkeletonCheckbox className="mr-16" />
                <SkeletonText width="80%" />
              </>
            ),
            header: (
              <>
                <SkeletonCheckbox className="mr-16" />
                <SkeletonText width="50%" />
              </>
            ),
          },
        ]}
        numberOfRows={5}
      />
    );
  }
  if (customFieldValuesQueryState.status !== 'success') {
    return null;
  }

  if (customFieldValuesQueryState.data.length === 0) {
    if (isMapSyncEnabled && syncOperation) {
      return (
        <Table<CustomFieldValue>
          columns={tableColumns}
          data={[]}
          getRowId={(row) => row.id}
          selectedRowIds={selectedValueIds}
          rowHeight="compact"
          maxHeight={400}
          onRowSelectionChange={() => {}}
          onAllRowsSelectionChange={() => {}}
          emptyState={{
            title: t('expenseCategories.mapSync.emptyPullingTitle', {
              integration: accountingSoftwareName,
            }),
            subtitle: t('expenseCategories.mapSync.emptyPullingSubtitle'),
          }}
        />
      );
    }

    return (
      <div className="flex flex-col items-center justify-center p-16">
        <p className="body-m text-content-primary-default mb-16">
          {isSearching
            ? t('customFields.noResults', { value: searchTerm })
            : t('customFields.values.title')}
        </p>
      </div>
    );
  }

  const handleRowSelectionChange = (rowId: string, isChecked: boolean) => {
    const alreadySelected = selectedValueIds.includes(rowId);

    if (isChecked && !alreadySelected) {
      const updated = [...selectedValueIds, rowId];
      onSelectedValuesChange(updated);
    } else if (!isChecked && alreadySelected) {
      const updated = selectedValueIds.filter((id) => id !== rowId);
      onSelectedValuesChange(updated);
    }
  };

  const handleAllRowsSelectionChange = (
    _: unknown,
    customFieldValueIds: string[],
    isChecked: boolean,
  ) => {
    if (isChecked) {
      const rowsToAdd = customFieldValuesQueryState.data.filter((value) =>
        customFieldValueIds.includes(value.id),
      );

      const merged = [...selectedValueIds];

      rowsToAdd.forEach((value) => {
        if (!merged.includes(value.id)) {
          merged.push(value.id);
        }
      });

      onSelectedValuesChange(merged);
    } else {
      const updated = selectedValueIds.filter(
        (id) => !customFieldValueIds.includes(id),
      );
      onSelectedValuesChange(updated);
    }
  };

  return (
    <>
      <Table<CustomFieldValue>
        columns={tableColumns}
        data={customFieldValuesQueryState.data}
        getRowId={(row) => row.id}
        selectedRowIds={selectedValueIds}
        rowHeight="compact"
        maxHeight={400}
        onRowSelectionChange={(_, rowId, isChecked) => {
          handleRowSelectionChange(rowId, isChecked);
        }}
        onAllRowsSelectionChange={(_, rowIds, isChecked) => {
          handleAllRowsSelectionChange(_, rowIds, isChecked);
        }}
        footer={
          customFieldValuesQueryState.hasNextPage && (
            <Button
              onClick={customFieldValuesQueryState.fetchNextPage}
              text={t('misc.loadMore')}
              variant="secondaryNeutral"
            />
          )
        }
      />
    </>
  );
};
